import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { CHAT_FUNCTION, INTEGRATIONS_FUNCTION } from '@/lib/chatFunction';

interface Integration {
  id: string;
  provider: 'supabase' | 'netlify' | 'github' | 'vercel';
  provider_email?: string;
  connected: boolean;
  created_at: string;
}

interface ProjectIntegration {
  id: string;
  provider: 'supabase' | 'netlify' | 'github' | 'vercel';
  external_project_id: string;
  external_project_name?: string;
  external_project_url?: string;
  status: string;
  created_at: string;
  metadata?: Record<string, unknown>;
}

interface SupabaseOrganization {
  id: string;
  name: string;
}

interface ConnectionStatus {
  connected: boolean;
  email?: string;
  fullName?: string;
  username?: string;
  isOAuth?: boolean;
}

interface DeploymentResult {
  success: boolean;
  siteUrl?: string;
  repoUrl?: string;
  adminUrl?: string;
  netlifyImportUrl?: string;
  buildStatus?: string;
  message?: string;
}

interface VercelDeploymentResult {
  success: boolean;
  deploymentUrl?: string;
  projectUrl?: string;      // Stable URL like projectname.vercel.app
  vercelProjectId?: string; // For redeployments
  webUrl?: string;
  inspectorUrl?: string;
  claimUrl?: string;
  message?: string;
  status?: string;
  error?: string;
}

export function useIntegrations() {
  const { session } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [projectIntegrations, setProjectIntegrations] = useState<ProjectIntegration[]>([]);
  const [supabaseOrganizations, setSupabaseOrganizations] = useState<SupabaseOrganization[]>([]);
  const [netlifyConnection, setNetlifyConnection] = useState<ConnectionStatus>({ connected: false });
  const [githubConnection, setGithubConnection] = useState<ConnectionStatus>({ connected: false });
  const [supabaseConnection, setSupabaseConnection] = useState<ConnectionStatus>({ connected: false });
  const [isDeploying, setIsDeploying] = useState(false);

  const callIntegrationsAPI = useCallback(async (action: string, params: Record<string, unknown> = {}) => {
    const { data, error } = await supabase.functions.invoke(INTEGRATIONS_FUNCTION, {
      body: { action, ...params },
    });

    if (error) throw new Error(error.message);
    if (data?.error) throw new Error(data.error);

    return data;
  }, []);

  // Fetch user integrations
  const fetchIntegrations = useCallback(async () => {
    if (!session) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await callIntegrationsAPI('getIntegrations');
      setIntegrations(data.integrations || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des intégrations');
    } finally {
      setIsLoading(false);
    }
  }, [session, callIntegrationsAPI]);

  // Check Supabase connection (via Management API or OAuth)
  const checkSupabaseConnection = useCallback(async () => {
    try {
      const data = await callIntegrationsAPI('checkSupabaseConnection');
      if (data.connected) {
        setSupabaseOrganizations(data.organizations || []);
        setSupabaseConnection({
          connected: true,
          email: data.email,
          username: data.username,
          isOAuth: data.isOAuth,
        });
      } else {
        setSupabaseConnection({ connected: false });
      }
      return data;
    } catch (err) {
      setSupabaseConnection({ connected: false });
      return { connected: false, error: err instanceof Error ? err.message : 'Erreur' };
    }
  }, [callIntegrationsAPI]);

  // Check Netlify connection
  const checkNetlifyConnection = useCallback(async () => {
    try {
      const data = await callIntegrationsAPI('checkNetlifyConnection');
      setNetlifyConnection({
        connected: data.connected,
        email: data.email,
        fullName: data.fullName,
      });
      return data;
    } catch (err) {
      setNetlifyConnection({ connected: false });
      return { connected: false };
    }
  }, [callIntegrationsAPI]);

  // Check GitHub connection
  const checkGitHubConnection = useCallback(async () => {
    try {
      const data = await callIntegrationsAPI('checkGitHubConnection');
      setGithubConnection({
        connected: data.connected,
        email: data.email,
        username: data.username,
      });
      return data;
    } catch (err) {
      setGithubConnection({ connected: false });
      return { connected: false };
    }
  }, [callIntegrationsAPI]);

  // Connect to Netlify (OAuth)
  // Optional context object to preserve state across OAuth redirect
  const connectNetlify = useCallback(async (context?: { projectId?: string; openDeploy?: boolean }) => {
    try {
      // Save context to sessionStorage before OAuth redirect
      if (context) {
        sessionStorage.setItem('oauth_return_context', JSON.stringify(context));
      }

      const redirectUri = `${window.location.origin}/auth/netlify/callback`;
      const data = await callIntegrationsAPI('getNetlifyAuthUrl', { redirectUri });

      if (data.authUrl) {
        window.location.href = data.authUrl;
      }
    } catch (err) {
      // Clean up on error
      sessionStorage.removeItem('oauth_return_context');
      throw err;
    }
  }, [callIntegrationsAPI]);

  // Connect to GitHub (OAuth)
  const connectGitHub = useCallback(async () => {
    try {
      const redirectUri = `${window.location.origin}/auth/github/callback`;
      const data = await callIntegrationsAPI('getGitHubAuthUrl', { redirectUri });

      if (data.authUrl) {
        window.location.href = data.authUrl;
      }
    } catch (err) {
      throw err;
    }
  }, [callIntegrationsAPI]);

  // Disconnect Netlify
  const disconnectNetlify = useCallback(async () => {
    try {
      await callIntegrationsAPI('disconnectNetlify');
      setNetlifyConnection({ connected: false });
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erreur' };
    }
  }, [callIntegrationsAPI]);

  // Disconnect GitHub
  const disconnectGitHub = useCallback(async () => {
    try {
      await callIntegrationsAPI('disconnectGitHub');
      setGithubConnection({ connected: false });
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erreur' };
    }
  }, [callIntegrationsAPI]);

  // Disconnect integration (legacy)
  const disconnectIntegration = useCallback(async (provider: 'supabase' | 'netlify' | 'github') => {
    if (provider === 'netlify') {
      return disconnectNetlify();
    }
    if (provider === 'github') {
      return disconnectGitHub();
    }
    if (provider === 'supabase') {
      return disconnectSupabase();
    }
    try {
      await callIntegrationsAPI('disconnectIntegration', { provider });
      setIntegrations(prev => prev.filter(i => i.provider !== provider));
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erreur' };
    }
  }, [callIntegrationsAPI, disconnectNetlify, disconnectGitHub]);

  // Connect to Supabase (OAuth)
  const connectSupabase = useCallback(async () => {
    try {
      const redirectUri = `${window.location.origin}/auth/supabase/callback`;
      const data = await callIntegrationsAPI('getSupabaseOAuthUrl', { redirectUri });

      if (data.authUrl && data.codeVerifier && data.state) {
        // Store code verifier and state for callback
        sessionStorage.setItem('supabase_code_verifier', data.codeVerifier);
        sessionStorage.setItem('supabase_oauth_state', data.state);
        window.location.href = data.authUrl;
      }
    } catch (err) {
      throw err;
    }
  }, [callIntegrationsAPI]);

  // Disconnect Supabase OAuth
  const disconnectSupabase = useCallback(async () => {
    try {
      await callIntegrationsAPI('disconnectSupabase');
      setSupabaseConnection({ connected: false });
      setSupabaseOrganizations([]);
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erreur' };
    }
  }, [callIntegrationsAPI]);

  // Get project integrations
  const fetchProjectIntegrations = useCallback(async (projectId: string) => {
    try {
      const data = await callIntegrationsAPI('getProjectIntegrations', { projectId });
      setProjectIntegrations(data.integrations || []);
      return data.integrations || [];
    } catch (err) {
      console.error('Error fetching project integrations:', err);
      return [];
    }
  }, [callIntegrationsAPI]);

  // Create Supabase project for a v0 project
  const createSupabaseProject = useCallback(async (
    projectId: string,
    projectName: string,
    organizationId: string,
    files?: Array<{ name: string; content: string }>
  ) => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await callIntegrationsAPI('createSupabaseProject', {
        projectId,
        projectName,
        organizationId,
        files,
      });

      await fetchProjectIntegrations(projectId);
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors de la création du projet Supabase';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [callIntegrationsAPI, fetchProjectIntegrations]);

  // Deploy to Netlify (legacy - iframe embed)
  const deployToNetlify = useCallback(async (
    projectId: string,
    projectName: string,
    files?: Array<{ name: string; content: string }>,
    demoUrl?: string
  ) => {
    setIsDeploying(true);
    setError(null);

    try {
      const data = await callIntegrationsAPI('deployToNetlify', {
        projectId,
        projectName,
        files,
        demoUrl,
      });

      await fetchProjectIntegrations(projectId);
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors du déploiement';
      setError(errorMessage);
      throw err;
    } finally {
      setIsDeploying(false);
    }
  }, [callIntegrationsAPI, fetchProjectIntegrations]);

  // Deploy to GitHub + Netlify CI/CD
  const deployToGitHubNetlify = useCallback(async (
    projectId: string,
    projectName: string,
    files: Array<{ name: string; content: string }>
  ): Promise<DeploymentResult> => {
    setIsDeploying(true);
    setError(null);

    try {
      const data = await callIntegrationsAPI('deployToGitHubNetlify', {
        projectId,
        projectName,
        files,
      });

      await fetchProjectIntegrations(projectId);
      return {
        success: true,
        siteUrl: data.siteUrl,
        repoUrl: data.repoUrl,
        adminUrl: data.adminUrl,
        buildStatus: data.buildStatus,
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors du déploiement';
      setError(errorMessage);
      throw err;
    } finally {
      setIsDeploying(false);
    }
  }, [callIntegrationsAPI, fetchProjectIntegrations]);

  // Deploy to Vercel using v0's native deployment API (100% identical to preview)
  const deployToVercelNative = useCallback(async (
    chatId: string,
    versionId: string,
    projectId?: string
  ): Promise<VercelDeploymentResult> => {
    setIsDeploying(true);
    setError(null);

    try {
      // Call v0-chat edge function with deployToVercel action
      const { data, error } = await supabase.functions.invoke(CHAT_FUNCTION, {
        body: {
          action: 'deployToVercel',
          chatId,
          versionId,
          projectId,
        },
      });

      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);

      if (projectId) {
        await fetchProjectIntegrations(projectId);
      }

      return {
        success: true,
        webUrl: data.webUrl,
        inspectorUrl: data.inspectorUrl,
        message: data.message,
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors du déploiement';
      setError(errorMessage);
      throw err;
    } finally {
      setIsDeploying(false);
    }
  }, [fetchProjectIntegrations]);

  // Deploy to Vercel with claim URL (user can transfer to their own account)
  const deployToVercelWithClaim = useCallback(async (
    chatId: string,
    versionId: string,
    projectId?: string
  ): Promise<VercelDeploymentResult> => {
    setIsDeploying(true);
    setError(null);

    try {
      // Call v0-chat edge function with deployToVercelWithClaim action
      const { data, error } = await supabase.functions.invoke(CHAT_FUNCTION, {
        body: {
          action: 'deployToVercelWithClaim',
          chatId,
          versionId,
          projectId,
        },
      });

      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);

      if (projectId) {
        await fetchProjectIntegrations(projectId);
      }

      return {
        success: true,
        deploymentUrl: data.deploymentUrl,
        webUrl: data.deploymentUrl,
        claimUrl: data.claimUrl,
        message: data.message,
        status: data.status,
        error: data.error,
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors du déploiement';
      setError(errorMessage);
      throw err;
    } finally {
      setIsDeploying(false);
    }
  }, [fetchProjectIntegrations]);

  // Legacy: Deploy to Vercel via integrations edge function (manual file generation)
  const deployToVercelClaim = useCallback(async (
    projectId: string,
    projectName: string,
    files: Array<{ name: string; content: string }>,
    vercelProjectId?: string  // Pass for redeployment on same project
  ): Promise<VercelDeploymentResult> => {
    setIsDeploying(true);
    setError(null);

    try {
      const data = await callIntegrationsAPI('deployToVercel', {
        projectId,
        projectName,
        files,
        vercelProjectId, // For redeployment
      });

      await fetchProjectIntegrations(projectId);
      return {
        success: true,
        deploymentUrl: data.deploymentUrl,
        projectUrl: data.projectUrl,         // Stable URL
        vercelProjectId: data.projectId,     // For next redeployment
        claimUrl: data.claimUrl,
        message: data.message,
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors du déploiement';
      setError(errorMessage);
      throw err;
    } finally {
      setIsDeploying(false);
    }
  }, [callIntegrationsAPI, fetchProjectIntegrations]);

  // List env vars for a v0 project
  const listEnvVars = useCallback(async (v0ProjectId: string) => {
    const { data, error } = await supabase.functions.invoke(CHAT_FUNCTION, {
      body: { action: 'listEnvVars', projectId: v0ProjectId },
    });

    if (error) throw new Error(error.message);
    return data?.envVars || [];
  }, []);

  // Create/update env vars for a v0 project
  const createEnvVars = useCallback(async (
    v0ProjectId: string,
    vars: Array<{ key: string; value: string }>
  ) => {
    const { data, error } = await supabase.functions.invoke(CHAT_FUNCTION, {
      body: { action: 'createEnvVars', projectId: v0ProjectId, envVars: vars },
    });

    if (error) throw new Error(error.message);
    return data;
  }, []);

  // Delete env vars from a v0 project
  const deleteEnvVars = useCallback(async (v0ProjectId: string, ids: string[]) => {
    const { data, error } = await supabase.functions.invoke(CHAT_FUNCTION, {
      body: { action: 'deleteEnvVars', projectId: v0ProjectId, envVarIds: ids },
    });

    if (error) throw new Error(error.message);
    return data;
  }, []);

  // Execute SQL on user's Supabase project
  const executeSQL = useCallback(async (v0ProjectId: string, sql: string) => {
    const data = await callIntegrationsAPI('executeSQL', {
      projectId: v0ProjectId,
      sql,
    });

    return data;
  }, [callIntegrationsAPI]);

  // Get database schema from user's Supabase project
  const getSchema = useCallback(async (v0ProjectId: string) => {
    const data = await callIntegrationsAPI('getSchema', {
      projectId: v0ProjectId,
    });

    return data;
  }, [callIntegrationsAPI]);

  // Refresh schema (fetch and store in metadata)
  const refreshSchema = useCallback(async (v0ProjectId: string) => {
    try {
      const result = await getSchema(v0ProjectId);
      return result?.schema || null;
    } catch (err) {
      console.error('Failed to refresh schema:', err);
      return null;
    }
  }, [getSchema]);

  return {
    isLoading,
    isDeploying,
    error,
    integrations,
    projectIntegrations,
    supabaseOrganizations,
    netlifyConnection,
    githubConnection,
    supabaseConnection,
    fetchIntegrations,
    checkSupabaseConnection,
    checkNetlifyConnection,
    checkGitHubConnection,
    connectNetlify,
    connectGitHub,
    connectSupabase,
    disconnectNetlify,
    disconnectGitHub,
    disconnectSupabase,
    disconnectIntegration,
    fetchProjectIntegrations,
    createSupabaseProject,
    deployToNetlify,
    deployToGitHubNetlify,
    deployToVercelNative,
    deployToVercelWithClaim,
    deployToVercelClaim,
    listEnvVars,
    createEnvVars,
    deleteEnvVars,
    executeSQL,
    getSchema,
    refreshSchema,
    isSupabaseConnected: supabaseConnection.connected || supabaseOrganizations.length > 0,
    isSupabaseOAuthConnected: supabaseConnection.isOAuth === true,
    isNetlifyConnected: netlifyConnection.connected,
    isGitHubConnected: githubConnection.connected,
  };
}
